import { List, TextField } from "@refinedev/antd";
import { useCustom } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useState } from "react";
import dayjs from "dayjs";

import s from "./PushAnalytics-List.module.scss";

const { Text, Title } = Typography;
const { TextArea } = Input;

type PushAnalyticsSummary = {
  usersTotal: number;
  connected: number;
  denied: number;
  notDetermined: number;
  noData: number;
  connectedStale: number;
  agreementForData: number;
  agreementForMarketing: number;
  anonDevices: number;
  anonConnected: number;
  anonDenied: number;
  anonNotDetermined: number;
};

type PushAnalyticsKind = {
  kindId: number;
  kindName?: string | null;
  subscribers: number;
  subscribersConnected: number;
};

type PushAnalyticsReport = {
  summary: PushAnalyticsSummary;
  kinds: PushAnalyticsKind[];
};

type UserPushStatus = "CONNECTED" | "DENIED" | "NOT_DETERMINED" | "NO_DATA";

type UserPushStatusInfo = {
  userId: string;
  status: UserPushStatus;
  updatedAt?: string | null;
  devicesCount: number;
  agreementForData?: boolean | null;
  agreementForMarketing?: boolean | null;
};

type UserPushStatusRow = {
  userId: string;
  statusInfo?: UserPushStatusInfo;
};

type MetricRow = {
  key: string;
  title: string;
  value?: number;
  total?: number;
  status?: "success" | "error" | "warning" | "default" | "processing";
  description: string;
};

const numberFormatter = new Intl.NumberFormat("ru-RU");

const pushStatusLabel: Record<UserPushStatus, string> = {
  CONNECTED: "Подключён",
  DENIED: "Запрещено",
  NOT_DETERMINED: "Не определено",
  NO_DATA: "Нет данных",
};

const pushStatusColor: Record<UserPushStatus, string> = {
  CONNECTED: "success",
  DENIED: "error",
  NOT_DETERMINED: "warning",
  NO_DATA: "default",
};

const toNumber = (value?: number | null) => value ?? 0;

const formatNumber = (value?: number | null) =>
  numberFormatter.format(toNumber(value));

const getPercent = (value?: number | null, total?: number | null) => {
  const safeTotal = toNumber(total);

  if (!safeTotal) {
    return 0;
  }

  return Math.round((toNumber(value) / safeTotal) * 100);
};

const renderPercent = (value?: number, total?: number) => {
  if (total === undefined) {
    return "—";
  }

  return `${getPercent(value, total)}%`;
};

const parseUserIds = (value: string) =>
  Array.from(
    new Set(
      value
        .split(/[\s,;]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );

const renderBoolean = (value?: boolean | null) => {
  if (value === undefined || value === null) {
    return "—";
  }

  return value ? "Да" : "Нет";
};

const renderUserPushStatus = (statusInfo?: UserPushStatusInfo) => {
  if (!statusInfo) {
    return <Text type="secondary">Нет ответа</Text>;
  }

  return (
    <Tag color={pushStatusColor[statusInfo.status]}>
      {pushStatusLabel[statusInfo.status]}
    </Tag>
  );
};

const MetricTable = ({
  title,
  description,
  dataSource,
  loading,
}: {
  title: string;
  description: string;
  dataSource: MetricRow[];
  loading: boolean;
}) => (
  <Card className={s.sectionCard}>
    <Space direction="vertical" size={16} className={s.fullWidth}>
      <div>
        <Title level={4}>{title}</Title>
        <Text type="secondary">{description}</Text>
      </div>

      <Table<MetricRow>
        dataSource={dataSource}
        rowKey="key"
        loading={loading}
        pagination={false}
        scroll={{ x: 760 }}
      >
        <Table.Column<MetricRow>
          dataIndex="title"
          title="Показатель"
          render={(value, record) => (
            <Space direction="vertical" size={0}>
              <TextField value={value} />
              <Text type="secondary">{record.description}</Text>
            </Space>
          )}
        />

        <Table.Column<MetricRow>
          dataIndex="value"
          title="Количество"
          render={(value) => formatNumber(value)}
        />

        <Table.Column<MetricRow>
          title="Доля"
          render={(_, record) => renderPercent(record.value, record.total)}
        />
      </Table>
    </Space>
  </Card>
);

export const PushAnalyticsList = () => {
  const [staleDays, setStaleDays] = useState<number | null>(60);
  const [userIdsInput, setUserIdsInput] = useState("");
  const [submittedUserIds, setSubmittedUserIds] = useState<string[]>([]);

  const { data, isLoading, isFetching, refetch, error } =
    useCustom<PushAnalyticsReport>({
      dataProviderName: "pushStatsDataProvider",
      url: "analytics",
      method: "get",
      meta: {
        params: {
          staleDays: staleDays || undefined,
        },
      },
    });

  const report = data?.data;
  const summary = report?.summary;
  const kinds = report?.kinds ?? [];
  const loading = isLoading || isFetching;
  const {
    data: userStatusesData,
    isFetching: isUserStatusesFetching,
    refetch: refetchUserStatuses,
    error: userStatusesError,
  } = useCustom<UserPushStatusInfo[]>({
    dataProviderName: "pushStatsDataProvider",
    url: "user-status",
    method: "get",
    meta: {
      params: {
        userIds: submittedUserIds.join(","),
      },
    },
    queryOptions: {
      enabled: submittedUserIds.length > 0,
    },
  });
  const userStatuses = userStatusesData?.data ?? [];
  const userStatusesById = new Map(
    userStatuses.map((status) => [status.userId, status])
  );
  const userStatusRows: UserPushStatusRow[] = submittedUserIds.map(
    (userId) => ({
      userId,
      statusInfo: userStatusesById.get(userId),
    })
  );

  const handleCheckUsers = () => {
    const nextUserIds = parseUserIds(userIdsInput);

    if (nextUserIds.join(",") === submittedUserIds.join(",")) {
      refetchUserStatuses();
      return;
    }

    setSubmittedUserIds(nextUserIds);
  };

  const systemRows: MetricRow[] = [
    {
      key: "CONNECTED",
      title: "Пуш дойдёт",
      value: summary?.connected,
      total: summary?.usersTotal,
      status: "success",
      description: "Есть системное разрешение и живой токен.",
    },
    {
      key: "DENIED",
      title: "Запрещено",
      value: summary?.denied,
      total: summary?.usersTotal,
      status: "error",
      description: "Установки есть, но уведомления запрещены во всех.",
    },
    {
      key: "NOT_DETERMINED",
      title: "Не определено",
      value: summary?.notDetermined,
      total: summary?.usersTotal,
      status: "warning",
      description: "Диалог не показан или канал не подтверждён.",
    },
    {
      key: "NO_DATA",
      title: "Нет данных",
      value: summary?.noData,
      total: summary?.usersTotal,
      status: "default",
      description: "Установок приложения у пользователя нет.",
    },
  ];

  const agreementRows: MetricRow[] = [
    {
      key: "USERS_TOTAL",
      title: "Всего пользователей",
      value: summary?.usersTotal,
      description: "Знаменатель для пользовательских долей.",
    },
    {
      key: "CONNECTED_STALE",
      title: "Подключены, но устарели",
      value: summary?.connectedStale,
      total: summary?.connected,
      status: "processing",
      description: "Подмножество подключённых старше выбранного срока.",
    },
    {
      key: "DATA",
      title: "Согласие на обработку данных",
      value: summary?.agreementForData,
      total: summary?.usersTotal,
      description: "Пользователи с согласием на обработку данных.",
    },
    {
      key: "MARKETING",
      title: "Согласие на маркетинг",
      value: summary?.agreementForMarketing,
      total: summary?.usersTotal,
      description: "Пользователи с маркетинговым согласием.",
    },
  ];

  const anonymousRows: MetricRow[] = [
    {
      key: "DEVICES",
      title: "Всего анонимных установок",
      value: summary?.anonDevices,
      description: "Установки без привязки к пользователю.",
    },
    {
      key: "CONNECTED",
      title: "Подключены",
      value: summary?.anonConnected,
      total: summary?.anonDevices,
      status: "success",
      description: "Анонимные установки с разрешением и живым токеном.",
    },
    {
      key: "DENIED",
      title: "Запрещено",
      value: summary?.anonDenied,
      total: summary?.anonDevices,
      status: "error",
      description: "Анонимные установки с системным запретом.",
    },
    {
      key: "NOT_DETERMINED",
      title: "Не определено",
      value: summary?.anonNotDetermined,
      total: summary?.anonDevices,
      status: "warning",
      description: "Анонимные установки без подтверждённого канала.",
    },
  ];

  const summaryContent = (
    <>
      <Space className={s.toolbar} wrap>
        <Text>Считать устаревшими через</Text>
        <InputNumber
          min={1}
          value={staleDays}
          onChange={setStaleDays}
          addonAfter="дней"
        />
        <Button
          icon={<ReloadOutlined />}
          loading={isFetching}
          onClick={() => refetch()}
        >
          Обновить
        </Button>
      </Space>

      {error && (
        <Alert
          className={s.alert}
          type="error"
          message="Не удалось загрузить аналитику пушей"
          showIcon
        />
      )}

      <div className={s.content}>
        <MetricTable
          title="Системное разрешение"
          description="Эти четыре строки складываются в общее количество пользователей."
          dataSource={systemRows}
          loading={loading}
        />

        <MetricTable
          title="Согласия и свежесть канала"
          description="Дополнительные пользовательские показатели из сводки."
          dataSource={agreementRows}
          loading={loading}
        />

        <MetricTable
          title="Анонимные установки"
          description="Эти значения считаются по установкам, а не по пользователям."
          dataSource={anonymousRows}
          loading={loading}
        />

        <Card className={s.sectionCard}>
          <Space direction="vertical" size={16} className={s.fullWidth}>
            <div>
              <Title level={4}>Типы уведомлений</Title>
              <Text type="secondary">
                Размер аудитории рассылки берётся из подключённых подписчиков.
              </Text>
            </div>

            <Table<PushAnalyticsKind>
              dataSource={kinds}
              rowKey="kindId"
              loading={loading}
              scroll={{ x: 920 }}
              pagination={{
                pageSize: 20,
                pageSizeOptions: [20, 50, 100],
              }}
              locale={{
                emptyText: (
                  <div style={{ margin: 56 }}>Типы уведомлений отсутствуют</div>
                ),
              }}
            >
              <Table.Column<PushAnalyticsKind>
                dataIndex="kindName"
                title="Тип уведомления"
                render={(value, record) => (
                  <Space direction="vertical" size={0}>
                    <TextField value={value || "Без названия"} />
                    <Text type="secondary">ID {record.kindId}</Text>
                  </Space>
                )}
              />

              <Table.Column<PushAnalyticsKind>
                dataIndex="subscribers"
                title="С галочкой"
                render={(value) => formatNumber(value)}
              />

              <Table.Column<PushAnalyticsKind>
                dataIndex="subscribersConnected"
                title="Пуш дойдёт"
                render={(value, record) => (
                  <Space direction="vertical" size={0}>
                    <Text>{formatNumber(value)}</Text>
                    <Text type="secondary">
                      {renderPercent(value, record.subscribers)}
                    </Text>
                  </Space>
                )}
              />

              <Table.Column<PushAnalyticsKind>
                title="Не дойдёт"
                render={(_, record) =>
                  formatNumber(record.subscribers - record.subscribersConnected)
                }
              />
            </Table>
          </Space>
        </Card>
      </div>
    </>
  );

  const usersContent = (
    <div className={s.content}>
      <Card className={s.sectionCard}>
        <Space direction="vertical" size={16} className={s.fullWidth}>
          <div>
            <Title level={4}>Аналитика пользователей</Title>
            <Text type="secondary">
              Вставьте один или несколько UUID пользователей через запятую,
              пробел или с новой строки.
            </Text>
          </div>

          <TextArea
            value={userIdsInput}
            onChange={(event) => setUserIdsInput(event.target.value)}
            rows={5}
            placeholder="Например: 3dcfa725-edf8-44b2-856e-7bd0cacd5e13"
          />

          <Space wrap>
            <Button
              type="primary"
              loading={isUserStatusesFetching}
              onClick={handleCheckUsers}
            >
              Проверить
            </Button>
            <Text type="secondary">
              К проверке: {formatNumber(parseUserIds(userIdsInput).length)}
            </Text>
          </Space>

          {userStatusesError && (
            <Alert
              type="error"
              message="Не удалось загрузить статусы пользователей"
              showIcon
            />
          )}

          <Table<UserPushStatusRow>
            dataSource={userStatusRows}
            rowKey="userId"
            loading={isUserStatusesFetching}
            scroll={{ x: 980 }}
            pagination={{
              pageSize: 20,
              pageSizeOptions: [20, 50, 100],
            }}
            locale={{
              emptyText: (
                <div style={{ margin: 56 }}>Добавьте пользователей</div>
              ),
            }}
          >
            <Table.Column<UserPushStatusRow>
              dataIndex="userId"
              title="Пользователь"
              render={(value) => <Text copyable>{value}</Text>}
            />

            <Table.Column<UserPushStatusRow>
              title="Пуш-статус"
              render={(_, record) => renderUserPushStatus(record.statusInfo)}
            />

            <Table.Column<UserPushStatusRow>
              title="Последнее обновление"
              render={(_, record) =>
                record.statusInfo?.updatedAt
                  ? dayjs(record.statusInfo.updatedAt).format(
                      "DD.MM.YYYY HH:mm"
                    )
                  : "—"
              }
            />

            <Table.Column<UserPushStatusRow>
              title="Устройств"
              render={(_, record) =>
                record.statusInfo?.status === "CONNECTED" &&
                record.statusInfo.devicesCount === 0
                  ? "—"
                  : formatNumber(record.statusInfo?.devicesCount)
              }
            />

            <Table.Column<UserPushStatusRow>
              title="Согласие на данные"
              render={(_, record) =>
                renderBoolean(record.statusInfo?.agreementForData)
              }
            />

            <Table.Column<UserPushStatusRow>
              title="Согласие на маркетинг"
              render={(_, record) =>
                renderBoolean(record.statusInfo?.agreementForMarketing)
              }
            />
          </Table>
        </Space>
      </Card>
    </div>
  );

  return (
    <List title="Аналитика пушей" canCreate={false} breadcrumb={false}>
      <Tabs
        items={[
          {
            key: "summary",
            label: "Сводка",
            children: summaryContent,
          },
          {
            key: "users",
            label: "Аналитика пользователей",
            children: usersContent,
          },
        ]}
      />
    </List>
  );
};
